/* eslint-disable prefer-destructuring */
/* eslint-disable default-case */

/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable class-methods-use-this */
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { getTotalNumAdults, getTotalNumChildren, getTotalNumInfants } from '@/utils/peopleUtils.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { ENVIRONMENT_NAME, getEnvironment } from '@/server/environment.ts';
import { SortValues } from '@/app/common/memsearch/types.ts';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import type { CuriosityParams } from '../index';
import {
  type ClickParams,
  type CommonParams,
  type FiltersParams,
  type HotelImagesParams,
  type InvalidDeepLinkParams,
  type PageViewParams,
  type RankingParams,
  type RedirectParams,
  type RedirectWidgetParams,
  type SearchWidgetParams,
  booleanValue,
  integerValues,
  removeExtraComma,
  validateCommaList,
} from './utils';
import { EventsEnum } from './utils/EventsEnum';
import type { PlatformEnum } from './utils/PlatformEnum';
import { sendEvent, sendAsyncEvent } from './utils/sendEvent';
import type { APIStatusEnum } from '../../common/reloadData/APIStatusEnum';
import { isSearchInThePast } from '../../components/NoResults/utils';
import type { Localization } from '../../common/Localization';
import { hasTouchEvents } from '../../utils/misc';

export const getLastDayOfMonth = ({ year, month }: { year: number; month: number }): number =>
  new Date(year, month, 0).getDate();

interface AnalyticsInterface {
  market: string;
  platform: PlatformEnum;
  brand?: string;
  url?: string;
  group?: number;
  searchId?: string;
  context?: string;
  status?: APIStatusEnum;
  AbortController: any;
  isServer?: boolean;
  commonData: CommonData;
  curiosityParams: CuriosityParams;
  localization: Localization;
  loadingTime?: number;
}

export class Analytics {
  private readonly market: string;

  private readonly platform: PlatformEnum;

  private readonly commonData: CommonData;

  private readonly curiosityParams: CuriosityParams;

  private readonly localization: Localization;

  private readonly brand?: string;

  private readonly url?: string;

  private readonly group?: number;

  private readonly userAgent?: string;

  private readonly userId?: string;

  private readonly sessionId?: string;

  private readonly searchId?: string;

  private readonly context?: string;

  private readonly status?: APIStatusEnum;

  private readonly isServer?: boolean;

  private readonly AbortController: any;

  private readonly loadingTime?: number;

  private readonly isBot: boolean;

  constructor({
    market,
    platform,
    commonData,
    curiosityParams,
    localization,
    brand,
    url,
    group,
    searchId,
    context,
    status,
    AbortController,
    isServer,
    loadingTime,
  }: AnalyticsInterface) {
    this.market = market;
    this.platform = platform;
    this.commonData = commonData;
    this.curiosityParams = curiosityParams;
    this.localization = localization;
    this.brand = brand;
    this.url = url;
    this.group = group;
    this.userAgent = commonData.UA;
    this.userId = commonData.userUid;
    this.sessionId = commonData.sessionId;
    this.searchId = searchId;
    this.context = context;
    this.status = status;
    this.AbortController = AbortController;
    this.isServer = isServer;
    this.loadingTime = loadingTime;
    this.isBot = commonData.isBot;
  }

  get analyticsEndpointsIntra(): { qa: string; prod: string } {
    return {
      qa: `http://oss-balancer.prd.bravofly.intra/oss-evo/curiosity-qa`,
      prod: `http://oss-balancer.prd.bravofly.intra/oss-evo/curiosity`,
    };
  }

  get analyticsEndpoints(): { qa: string; prod: string } {
    return {
      qa: `https://services.lastminute.com/oss-evo/curiosity-qa`,
      prod: `https://services.lastminute.com/oss-evo/curiosity`,
    };
  }

  get os(): string {
    let os = 'Unknown';
    const userAgent: string = canUseDOM ? window.navigator.userAgent || '' : this.userAgent || '';

    if (userAgent.indexOf('Windows NT 10.0') !== -1) os = 'Windows 10';
    if (userAgent.indexOf('Windows NT 6.3') !== -1) os = 'Windows 8.1';
    if (userAgent.indexOf('Windows NT 6.2') !== -1) os = 'Windows 8';
    if (userAgent.indexOf('Windows NT 6.1') !== -1) os = 'Windows 7';
    if (userAgent.indexOf('Windows NT 6.0') !== -1) os = 'Windows Vista';
    if (userAgent.indexOf('Windows NT 5.1') !== -1) os = 'Windows XP';
    if (userAgent.indexOf('Windows NT 5.0') !== -1) os = 'Windows 2000';
    if (userAgent.indexOf('Mac') !== -1) os = 'Mac/iOS';
    if (userAgent.indexOf('X11') !== -1) os = 'UNIX';
    if (userAgent.indexOf('Linux') !== -1) os = 'Linux';

    return os;
  }

  get device(): string {
    if (canUseDOM) {
      const { innerWidth } = window;
      if (hasTouchEvents() && innerWidth < 768) return 'mobile';
      if (hasTouchEvents() && innerWidth >= 768 && innerWidth <= 1024) return 'tablet';
      return 'desktop';
    }
    const isMobileDevice = this.userAgent
      ? /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(this.userAgent)
      : false;
    return isMobileDevice ? 'mobile' : 'desktop';
  }

  get commonParams(): CommonParams {
    const url = this.url ? this.url : canUseDOM ? window.location.search : '';
    const queryString = url.split('?')[1];
    const params = new URLSearchParams(queryString);
    const paramsObject: CommonParams = {
      group: this.group,
    };
    params.forEach((value, key) => {
      switch (key) {
        case URLParameters.ABTEST:
          paramsObject.ab_test = value;
          break;
        case URLParameters.TRAFFIC_SUBSOURCE:
          paramsObject.traffic_subsource = value;
          break;
        case URLParameters.TRAFFIC_SOURCE:
          paramsObject.traffic_source = value;
          break;
        case URLParameters.SORT:
          paramsObject.sort = value;
          break;
        case URLParameters.SOURCE:
          paramsObject.traffic_source = value;
          break;
        case URLParameters.SUBSOURCE:
          paramsObject.traffic_subsource = value;
          break;
      }
    });
    // Sort value default: "recommended"
    if (!paramsObject.sort) {
      paramsObject.sort = SortValues.Recommended;
    }
    return paramsObject;
  }

  get searchParams(): SearchWidgetParams {
    const url = this.url ? this.url : canUseDOM ? window.location.search : '';
    const queryString = url.split('?')[1];
    const params = new URLSearchParams(queryString);
    const paramsObject: SearchWidgetParams = {};
    params.forEach((value, key) => {
      switch (key) {
        case URLParameters.ADULTS: {
          paramsObject.people_adults = getTotalNumAdults(value);
          break;
        }
        case URLParameters.AGES: {
          paramsObject.people_child = getTotalNumChildren(value);
          paramsObject.people_infant = getTotalNumInfants(value);
          break;
        }
        case URLParameters.DATE_FROM: {
          if (value.match(/^\d{4}-\d{2}-\d{2}$/) !== null) {
            const departureDates = this.parseDateParam(value).split(',');
            paramsObject.departure_date_from = departureDates[0];
            paramsObject.departure_date_to = departureDates[1];
          } else if (value === 'A') {
            paramsObject.departure_date_from = value;
            paramsObject.departure_date_to = value;
          } else if (value.indexOf('M,') !== -1) {
            const months = value.toString().split('M,')[1].split(',');
            const monthsLength = months.length;
            if (monthsLength === 1) {
              const year = parseInt(months[0].split('-')[0], 10);
              const month = parseInt(months[0].split('-')[1], 10);
              const lastDayOfMonth = getLastDayOfMonth({ year, month });
              paramsObject.departure_date_from = `${months[0]}-01`;
              paramsObject.departure_date_to = `${months[0]}-${lastDayOfMonth}`;
            } else {
              const year = parseInt(months[monthsLength - 1].split('-')[0], 10);
              const month = parseInt(months[monthsLength - 1].split('-')[1], 10);
              const lastDayOfMonth = getLastDayOfMonth({ year, month });
              paramsObject.departure_date_from = `${months[0]}-01`;
              paramsObject.departure_date_to = `${months[monthsLength - 1]}-${lastDayOfMonth}`;
            }
          } else {
            const departureDates = this.parseDateParam(value).split(',');
            paramsObject.departure_date_from = departureDates[0];
            paramsObject.departure_date_to = departureDates[1];
          }
          break;
        }
        case URLParameters.DATE_TO: {
          const returnDates = this.parseDateParam(value).split(',');
          if (returnDates[0] === 'oneway') {
            // paramsObject.oneway = 1;
          } else if (returnDates[0] === 'T') {
            paramsObject.nights_from = parseInt(returnDates[1], 10);
            paramsObject.nights_to = parseInt(returnDates[2], 10);
          } else {
            paramsObject.return_date_from = returnDates[0];
            paramsObject.return_date_to = returnDates[1];
          }
          break;
        }
        case URLParameters.DESTINATION:
          paramsObject.destination = value;
          break;
        case URLParameters.LEISURE:
          paramsObject.leisure = value.replace(/L/g, '');
          break;
        case URLParameters.ORIGIN:
          if (value && value !== 'null' && value !== '') paramsObject.origin = value;
          break;
      }
    });
    return paramsObject;
  }

  get filtersParams(): FiltersParams {
    const url = this.url ? this.url : canUseDOM ? window.location.search : '';
    const queryString = url.split('?')[1];
    const params = new URLSearchParams(queryString);
    const paramsObject: FiltersParams = {};
    params.forEach((value, key) => {
      if (value !== '') {
        switch (key) {
          case URLParameters.AIRLINES:
            if (validateCommaList(value)) paramsObject.filters_values_airlines = removeExtraComma(value);
            break;
          case URLParameters.ALTERNATIVE_ACCOMODATION:
            paramsObject.filters_alt_accomod = booleanValue(value);
            break;
          case URLParameters.DEPARTURE_FLIGHTS:
            paramsObject.filters_values_departure_flights = booleanValue(value);
            break;
          case URLParameters.DEPARTURE_TIME_OUTBOUND: {
            const { min, max } = integerValues(value);
            paramsObject.filters_values_departure_time_outbound_from = min;
            paramsObject.filters_values_departure_time_outbound_to = max;
            break;
          }
          case URLParameters.DEPARTURE_TIME_RETURN: {
            const { min, max } = integerValues(value);
            paramsObject.filters_values_return_time_outbound_from = min;
            paramsObject.filters_values_return_time_outbound_to = max;
            break;
          }
          case URLParameters.DIRECT:
            paramsObject.filters_values_direct = booleanValue(value);
            break;
          case URLParameters.FLASH_SALES:
            paramsObject.filters_flash_sales = booleanValue(value);
            break;
          case URLParameters.MY_PRICE:
            paramsObject.filters_values_myprice = booleanValue(value);
            break;
          case URLParameters.TOP_SECRET_HOTEL:
            paramsObject.filters_values_top_secret_hotel = booleanValue(value);
            break;
          case URLParameters.FLIGHT_DURATION: {
            const { min, max } = integerValues(value);
            paramsObject.filters_values_flight_duration_from = min;
            paramsObject.filters_values_flight_duration_to = max;
            break;
          }
          case URLParameters.FREE_CANCELLATION:
            paramsObject.filters_free_cancel = booleanValue(value);
            break;
          case URLParameters.BOOK_NOW_PAY_LATER:
            paramsObject.filters_book_now_pay_later = booleanValue(value);
            break;
          case URLParameters.MEAL:
            if (validateCommaList(value)) paramsObject.filters_values_meal = removeExtraComma(value);
            break;
          case URLParameters.OUTBOUND_ARRIVAL_AIRPORTS:
            if (validateCommaList(value))
              paramsObject.filters_values_outbound_arrival_airports = removeExtraComma(value);
            break;
          case URLParameters.OVERNIGHT_FLIGHTS:
            paramsObject.filters_overnight_selected = booleanValue(value);
            break;
          case URLParameters.OUTBOUND_CONNECTIONS:
            if (validateCommaList(value)) paramsObject.filters_values_outbound_connections = removeExtraComma(value);
            break;
          case URLParameters.OUTBOUND_DEPARTURE_AIRPORTS:
            if (validateCommaList(value))
              paramsObject.filters_values_outbound_departure_airports = removeExtraComma(value);
            break;
          case URLParameters.PRICE: {
            const { min, max } = integerValues(value);
            paramsObject.filters_values_price_from = min;
            paramsObject.filters_values_price_to = max;
            break;
          }
          case URLParameters.RATING:
            if (validateCommaList(value)) paramsObject.filters_values_rating = removeExtraComma(value);
            break;
          case URLParameters.RETURN_ARRIVAL_AIRPORTS:
            if (validateCommaList(value)) paramsObject.filters_values_return_arrival_airports = removeExtraComma(value);
            break;
          case URLParameters.RETURN_CONNECTIONS:
            if (validateCommaList(value)) paramsObject.filters_values_return_connections = removeExtraComma(value);
            break;
          case URLParameters.RETURN_DEPARTURE_AIRPORTS:
            if (validateCommaList(value))
              paramsObject.filters_values_return_departure_airports = removeExtraComma(value);
            break;
          case URLParameters.RETURN_FLIGHTS:
            if (validateCommaList(value)) paramsObject.filters_values_return_flights = booleanValue(value);
            break;
          case URLParameters.STARS:
            if (validateCommaList(value)) paramsObject.filters_values_hotel_stars = removeExtraComma(value);
            break;
          case URLParameters.TRAVEL_FLEXIBILITY:
            if (validateCommaList(value)) paramsObject.filters_values_travel_flexibility = booleanValue(value);
            break;
          case URLParameters.ACCOMODATION_TYPE:
            if (validateCommaList(value)) paramsObject.filters_values_accomodation_type = removeExtraComma(value);
            break;
          case URLParameters.ACCOMODATION_FACILITIES:
            if (validateCommaList(value)) paramsObject.filters_values_accomodation_facilities = removeExtraComma(value);
            break;
          case URLParameters.BLUE_FLAG:
            paramsObject.filters_leisure_blue_flag = booleanValue(value);
            break;
          case URLParameters.SUSTAINABILITY_CERTIFICATE:
            paramsObject.filters_sustainability_certificate = booleanValue(value);
            break;
          case URLParameters.BEACH_TYPE:
            if (validateCommaList(value)) paramsObject.filters_leisure_beach_type = removeExtraComma(value);
            break;
          case URLParameters.CLIMATE:
            if (validateCommaList(value)) paramsObject.filters_leisure_climate = removeExtraComma(value);
            break;
          case URLParameters.FAUNA:
            if (validateCommaList(value)) paramsObject.filters_leisure_fauna = removeExtraComma(value);
            break;
          case URLParameters.WATERSPORT:
            if (validateCommaList(value)) paramsObject.filters_leisure_watersport = removeExtraComma(value);
            break;
          case URLParameters.WATER_WAVES:
            if (validateCommaList(value)) paramsObject.filters_leisure_waterwaves = removeExtraComma(value);
            break;
          case URLParameters.ENTERTAINMENTS:
            if (validateCommaList(value)) paramsObject.filters_leisure_entertainments = removeExtraComma(value);
            break;
          case URLParameters.ACCOMODATION_SKILIFT:
            if (validateCommaList(value)) paramsObject.filters_values_accomodation_skilift = removeExtraComma(value);
            break;
          case URLParameters.ACCOMODATION_DISTRICT:
            if (validateCommaList(value)) paramsObject.filters_values_accomodation_district = removeExtraComma(value);
            break;
          case URLParameters.ACCOMODATION_STATION:
            if (validateCommaList(value)) paramsObject.filters_values_accomodation_station = removeExtraComma(value);
            break;
          case URLParameters.ACCOMODATION_CHAIN:
            if (validateCommaList(value)) paramsObject.filters_values_accomodation_chain = removeExtraComma(value);
            break;
          case URLParameters.ACCOMODATION_THEME:
            if (validateCommaList(value)) paramsObject.filters_values_accomodation_theme = removeExtraComma(value);
            break;
          case URLParameters.ACCOMODATION_NAME:
            if (validateCommaList(value)) paramsObject.filters_values_accomodation_name = removeExtraComma(value);
            break;
          case URLParameters.ACCOMODATION_BEACH:
            if (validateCommaList(value)) paramsObject.filters_values_accomodation_beach = removeExtraComma(value);
            break;
        }
      }
    });
    paramsObject.filters_applied = Object.keys(paramsObject).length > 0 ? 1 : 0;
    return paramsObject;
  }

  get user_id(): string | undefined {
    return this.userId;
  }

  get session_id(): string | undefined {
    return this.sessionId;
  }

  get is_bot(): boolean {
    return this.isBot;
  }

  parseDateParam(value: string): string {
    const fieldValue = value;
    if (fieldValue && fieldValue.indexOf('today') !== -1) {
      const valuesArray = fieldValue.split(',');
      const startDateValue = valuesArray[0];
      const endDateValue = valuesArray[1];
      const startDaysToAdd = parseInt(startDateValue.split(' ')[1], 10) || 0;
      const currenStartDate = new Date();
      currenStartDate.setDate(currenStartDate.getDate() + startDaysToAdd);
      const startDate = currenStartDate.toISOString().split('T')[0];

      if (endDateValue) {
        const endDaysToAdd = parseInt(endDateValue.split(' ')[1], 10) || 0;
        const currentEndDate = new Date();
        currentEndDate.setDate(currentEndDate.getDate() + endDaysToAdd);
        const endDate = currentEndDate.toISOString().split('T')[0];

        return `${startDate},${endDate}`;
      }

      return startDate;
    }
    return fieldValue;
  }

  searchEvent({ offset, type }: { offset: number; type: EventsEnum }): boolean {
    const {
      market,
      platform,
      os,
      device,
      commonParams,
      searchParams,
      filtersParams,
      searchId,
      user_id,
      session_id,
      brand,
      context,
      status,
      loadingTime,
    } = this;
    if (
      (type === EventsEnum.DP_SEARCH || type === EventsEnum.FF_SEARCH || type === EventsEnum.DP_NORESULTS) &&
      (searchParams[URLParameters.ORIGIN] === '' || !searchParams[URLParameters.ORIGIN])
    )
      return false;
    if (searchParams[URLParameters.DESTINATION] === '' || !searchParams[URLParameters.DESTINATION]) return false;
    if (this.url) {
      const queryString = this.url.split('?')[1];
      const params = new URLSearchParams(queryString);
      const paramsObject: SearchParams = {};
      params.forEach((value, key) => {
        paramsObject[key] = value;
      });
      if (paramsObject && isSearchInThePast(paramsObject)) return false;
    }

    this.sendEvent({
      type,
      user_id,
      session_id,
      search_id: searchId,
      platform,
      market,
      device,
      os,
      context: context || '',
      offset,
      brand,
      loading_time: loadingTime,
      ...commonParams,
      ...searchParams,
      ...filtersParams,
      ...(type === EventsEnum.DP_NORESULTS || type === EventsEnum.HO_NORESULTS ? { status } : {}),
    });
    return true;
  }

  redirectEvent({
    offset,
    type,
    redirectParams,
  }: {
    offset?: number;
    type: EventsEnum;
    redirectParams: RedirectParams;
  }): void {
    const {
      market,
      platform,
      os,
      device,
      commonParams,
      searchParams,
      filtersParams,
      searchId,
      user_id,
      session_id,
      brand,
    } = this;
    this.sendEvent({
      type,
      user_id,
      session_id,
      search_id: searchId,
      platform,
      market,
      device,
      os,
      context: '',
      offset,
      brand,
      ...commonParams,
      ...searchParams,
      ...filtersParams,
      ...redirectParams,
    });
  }

  redirectWidgetEvent({
    offset,
    type,
    redirectParams,
  }: {
    offset?: number;
    type: EventsEnum;
    redirectParams: RedirectWidgetParams;
  }): void {
    const { market, platform, os, device, commonParams, filtersParams, searchId, user_id, session_id, brand } = this;
    this.sendEvent({
      type,
      user_id,
      session_id,
      search_id: searchId,
      platform,
      market,
      device,
      os,
      context: '',
      offset,
      brand,
      ...commonParams,
      ...filtersParams,
      ...redirectParams,
    });
  }

  sendClickEvent({ clickParams }: { clickParams: ClickParams }): void {
    const { market, platform, os, device, user_id, session_id, brand, commonParams } = this;
    this.sendEvent({
      type: EventsEnum.CLICK,
      user_id,
      session_id,
      platform,
      market,
      device,
      os,
      brand,
      ...commonParams,
      ...clickParams,
    });
  }

  sendPageViewEvent({ pageViewParams }: { pageViewParams: PageViewParams }): void {
    const { market, platform, os, device, user_id, session_id, commonParams, brand, userAgent, loadingTime } = this;
    this.sendEvent({
      type: EventsEnum.PAGEVIEW,
      user_id,
      session_id,
      platform,
      market,
      device,
      os,
      brand,
      user_agent: userAgent,
      loading_time: loadingTime,
      ...commonParams,
      ...pageViewParams,
    });
  }

  sendInvalidDeepLinkEvent({ invalidDeepLinkParams }: { invalidDeepLinkParams: InvalidDeepLinkParams }): void {
    const { market, platform, os, device, user_id, session_id, commonParams, brand } = this;
    this.sendEvent({
      type: EventsEnum.INVALID_DEEPLINK,
      user_id,
      session_id,
      platform,
      market,
      device,
      os,
      brand,
      ...commonParams,
      ...invalidDeepLinkParams,
    });
  }

  sendRankingEvent({ rankingParams }: { rankingParams: RankingParams }): void {
    const { searchParams, searchId, market, platform, os, device, user_id, session_id, commonParams, brand } = this;
    this.sendEvent({
      type: EventsEnum.RANKING,
      user_id,
      session_id,
      platform,
      market,
      device,
      os,
      brand,
      search_id: searchId,
      sort_type: commonParams.sort,
      origin: searchParams?.origin,
      destination: searchParams?.destination,
      people_adults: searchParams?.people_adults,
      people_child: searchParams?.people_child || 0,
      people_infant: searchParams?.people_infant || 0,
      date_from: searchParams?.departure_date_from,
      date_to: searchParams?.return_date_from || searchParams?.departure_date_to,
      offer_currency: this.localization.currency,
      ...commonParams,
      ...rankingParams,
    });
  }

  hotelImages(hotelImagesParams: HotelImagesParams): boolean {
    const { market, searchParams, searchId, user_id, session_id } = this;

    this.sendEvent({
      type: EventsEnum.HOTEL_IMAGES,
      user_id,
      session_id,
      search_id: searchId,
      market,
      origin: searchParams?.origin,
      destination: searchParams?.destination,
      people_adults: searchParams?.people_adults,
      people_child: searchParams?.people_child,
      people_infant: searchParams?.people_infant,
      date_from: searchParams?.departure_date_from,
      date_to: searchParams?.return_date_from || searchParams?.departure_date_to,
      offer_currency: this.localization.currency,
      ...hotelImagesParams,
    });
    return true;
  }

  async sendEvent(dataRaw: any) {
    const {
      localization: {
        configValuesApp: { curiosityEventsEnabled },
      },
      commonData: { abTests },
      isServer,
      is_bot,
    } = this;

    if (!curiosityEventsEnabled) return;

    const abTestValues =
      Array.isArray(abTests) && abTests.length > 0
        ? {
            saab_test_id: 0,
            saab_test_variant: abTests[0].version,
          }
        : {};
    const data = { ...dataRaw, ...abTestValues };

    sendEvent({
      environment: getEnvironment(ENVIRONMENT_NAME.APP, this.localization?.environments),
      isServer: !!isServer,
      data,
      AbortController: this.AbortController,
      isBot: is_bot,
    });
  }

  sendDetailsEvent(dataRaw: any) {
    const { user_id, session_id, searchId, brand, market, device, os } = this;

    this.sendEvent({
      user_id,
      session_id,
      search_id: searchId,
      brand,
      market,
      device,
      os,
      ...dataRaw,
    });
  }

  async sendAsyncDetailsEvent(dataRaw: any): Promise<any> {
    const {
      localization: {
        configValuesApp: { curiosityEventsEnabled },
      },
      user_id,
      session_id,
      searchId,
      brand,
      market,
      device,
      os,
    } = this;

    if (!curiosityEventsEnabled) return Promise.resolve();

    const eventData = {
      user_id,
      session_id,
      search_id: searchId,
      brand,
      market,
      device,
      os,
      ...dataRaw,
    };

    const {
      commonData: { abTests },
      isServer,
      is_bot,
    } = this;

    const abTestValues =
      Array.isArray(abTests) && abTests.length > 0
        ? {
            saab_test_id: 0,
            saab_test_variant: abTests[0].version,
          }
        : {};
    const data = { ...eventData, ...abTestValues };

    return sendAsyncEvent({
      environment: getEnvironment(ENVIRONMENT_NAME.APP, this.localization?.environments),
      isServer: !!isServer,
      data,
      AbortController: this.AbortController,
      isBot: is_bot,
    });
  }
}
